import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { queryAuditLogs, AuditLogEntry, AuditLogQueryParams } from '../../store/actions/AuditLogActions';
import { showAuditLogDetailModal } from '../../store/actions/modalActions';
import { PiClockCounterClockwiseLight, PiUserLight, PiEyeLight } from 'react-icons/pi';
import Table from '../shared/TailwindComponents/Table';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Badge from '../shared/TailwindComponents/Badge';
import { Tooltip } from '@mui/material';
import { formatDateTime } from '../../utils/functions';
import AuditLogPanel from './AuditLogPanel';
import GlassCard from '../shared/brand/GlassCard';
import PersonCell from '../Admin/people/PersonCell';
import useAvatarDirectory from '../Admin/people/useAvatarDirectory';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, activityLogTourSteps } from '../shared/FeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';
import { PAGE_SIZE_OPTIONS } from '../shared/TailwindComponents/TablePagination';
import { LIST_SECTION_SHELL, LIST_SECTION_CHROME, LIST_SECTION_SURFACE } from '../Settings/framework/listSectionLayout';

const ENTITY_TYPE_OPTIONS = [
  { value: 'Client', label: 'Client' },
  { value: 'ClientBilling', label: 'Client Billing' },
  { value: 'ClientDocument', label: 'Client Document' },
  { value: 'ClientLawyer', label: 'Client Lawyer' },
  { value: 'Matter', label: 'Matter' },
  { value: 'MatterClient', label: 'Matter Client' },
  { value: 'MatterCollaborator', label: 'Matter Collaborator' },
  { value: 'MatterDocument', label: 'Matter Document' },
  { value: 'MatterDraft', label: 'Matter Draft' },
  { value: 'MatterInsight', label: 'Matter Insight' },
  { value: 'MatterNote', label: 'Matter Note' },
  { value: 'MatterQuestion', label: 'Matter Question' },
  { value: 'MatterSource', label: 'Matter Source' },
  { value: 'MatterTask', label: 'Matter Task' },
  { value: 'File', label: 'File' },
  { value: 'Folder', label: 'Folder' },
  { value: 'Task', label: 'Task' },
  { value: 'table', label: 'Table' },
  { value: 'Tag', label: 'Tag' },
  { value: 'RecycleBin', label: 'Recycle Bin' },
  { value: 'CalendarEvent', label: 'Calendar Event' },
  { value: 'Team', label: 'Team' },
  { value: 'TeamMember', label: 'Team Member' },
  { value: 'TeamDocument', label: 'Team Document' },
  { value: 'TeamFeedEvent', label: 'Team Feed Event' },
  { value: 'TeamFeedComment', label: 'Team Feed Comment' },
  { value: 'Department', label: 'Department' },
  { value: 'DepartmentMember', label: 'Department Member' },
  { value: 'DepartmentDocument', label: 'Department Document' },
  { value: 'DepartmentFeedEvent', label: 'Department Feed Event' },
  { value: 'DepartmentFeedComment', label: 'Department Feed Comment' },
  { value: 'Organization', label: 'Organization' },
  { value: 'User', label: 'User' },
  { value: 'Role', label: 'Role' },
  { value: 'Permission', label: 'Permission' },
  { value: 'UserRole', label: 'User Role' },
  { value: 'UserPermission', label: 'User Permission' },
  { value: 'Device', label: 'Device' },
  { value: 'License', label: 'License' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Payout', label: 'Payout' },
  { value: 'BillingRate', label: 'Billing Rate' },
  { value: 'Expense', label: 'Expense' },
  { value: 'Distribution', label: 'Distribution' },
  { value: 'Invitation', label: 'Invitation' },
  { value: 'JoinRequest', label: 'Join Request' },
  { value: 'Feedback', label: 'Feedback' },
  { value: 'SupportTicket', label: 'Support Ticket' },
  { value: 'SupportTicketComment', label: 'Support Comment' },
  { value: 'BulkImport', label: 'Bulk Import' },
  { value: 'research', label: 'Research' },
  { value: 'review_session', label: 'Review Session' },
  { value: 'playbook', label: 'Playbook' },
  { value: 'table_document', label: 'Table Document' },
  { value: 'datasource', label: 'Data Source' },
];

const ACTION_OPTIONS = [
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
  { value: 'restore', label: 'Restored' },
  { value: 'share', label: 'Shared' },
  { value: 'rename', label: 'Renamed' },
  { value: 'assign', label: 'Assigned' },
  { value: 'remove', label: 'Removed' },
  { value: 'collaborator_add', label: 'Access Granted' },
  { value: 'collaborator_remove', label: 'Access Removed' },
  { value: 'collaborator_access_update', label: 'Access Level Updated' },
  { value: 'collaborator_role_update', label: 'Role Updated' },
  { value: 'add_attachment', label: 'Attachment Added' },
  { value: 'delete_attachment', label: 'Attachment Deleted' },
  { value: 'update_status', label: 'Status Changed' },
  { value: 'update_description', label: 'Description Updated' },
  { value: 'decision', label: 'Decision' },
  { value: 'empty_trash', label: 'Trash Emptied' },
  { value: 'restore_from_trash', label: 'Restored from Trash' },
  { value: 'restore_version', label: 'Version Restored' },
  { value: 'bulk_delete', label: 'Bulk Deleted' },
  { value: 'duplicated', label: 'Duplicated' },
  { value: 'user_assigned', label: 'User Assigned' },
  { value: 'user_revoked', label: 'User Revoked' },
  { value: 'trial_assigned', label: 'Trial Assigned' },
  { value: 'update_retention', label: 'Retention Updated' },
  { value: 'session_logout', label: 'Session Logout' },
  { value: 'all_sessions_logout', label: 'All Sessions Logout' },
  { value: 'logout_all', label: 'Logged Out All Devices' },
  { value: 'permanent_delete', label: 'Permanently Deleted' },
  { value: 'set_preferences', label: 'Preferences Updated' },
  { value: 'update_info', label: 'Info Updated' },
  { value: 'save', label: 'Saved' },
  { value: 'manage_access', label: 'Access Managed' },
];

const getEntityBadgeColor = (entityType: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
  const colorMap: Record<string, 'gray' | 'blue' | 'yellow' | 'green' | 'red'> = {
    Client: 'blue',
    ClientBilling: 'blue',
    ClientDocument: 'blue',
    ClientLawyer: 'blue',
    Matter: 'green',
    MatterClient: 'green',
    MatterCollaborator: 'green',
    MatterDocument: 'green',
    MatterDraft: 'green',
    MatterInsight: 'green',
    MatterNote: 'green',
    MatterQuestion: 'green',
    MatterSource: 'green',
    MatterTask: 'green',
    File: 'gray',
    Folder: 'gray',
    Task: 'yellow',
    table: 'green',
    Tag: 'gray',
    Team: 'blue',
    TeamMember: 'blue',
    Department: 'blue',
    DepartmentMember: 'blue',
    Organization: 'yellow',
    User: 'yellow',
    Role: 'yellow',
    Permission: 'yellow',
    RecycleBin: 'red',
    License: 'yellow',
    Device: 'gray',
    research: 'blue',
    review_session: 'green',
    playbook: 'green',
    table_document: 'green',
    datasource: 'gray',
  };
  return colorMap[entityType] || 'gray';
};

const getActionBadgeColor = (action: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
  if (action.includes('creat') || action.includes('upload') || action.includes('restor') || action.includes('add')) return 'green';
  if (action.includes('delet') || action.includes('permanent') || action.includes('logout')) return 'red';
  if (action.includes('updat') || action.includes('chang') || action.includes('renam') || action === 'update' || action === 'save' || action.includes('preference') || action.includes('info')) return 'blue';
  if (action.includes('archiv') || action.includes('reset')) return 'yellow';
  return 'gray';
};

interface AuditLogPageProps {
  embedded?: boolean;
  defaultEntityType?: string;
  userOnly?: boolean;
}

const AuditLogPage: React.FC<AuditLogPageProps> = ({ embedded = false, defaultEntityType, userOnly = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const avatarDirectory = useAvatarDirectory();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'activity-log',
    steps: activityLogTourSteps,
    autoStartDelay: 600,
  });

  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>(
    defaultEntityType ? { entity_type: [defaultEntityType] } : {}
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [auditTarget, setAuditTarget] = useState<{ entityType: string; entityId: string; name: string } | null>(null);

  const filters = [
    { id: 'entity_type', name: t('Resource Type'), options: ENTITY_TYPE_OPTIONS.map(o => ({ ...o, label: t(o.label) })) },
    { id: 'action', name: t('Action'), options: ACTION_OPTIONS.map(o => ({ ...o, label: t(o.label) })) },
  ];

  const sortOptions = [
    { value: 'created_at_desc', label: t('Newest First'), current: sortBy === 'created_at' && sortOrder === 'desc' },
    { value: 'created_at_asc', label: t('Oldest First'), current: sortBy === 'created_at' && sortOrder === 'asc' },
    { value: 'entity_type_asc', label: t('Resource Type (A-Z)'), current: sortBy === 'entity_type' && sortOrder === 'asc' },
    { value: 'entity_type_desc', label: t('Resource Type (Z-A)'), current: sortBy === 'entity_type' && sortOrder === 'desc' },
    { value: 'action_asc', label: t('Action (A-Z)'), current: sortBy === 'action' && sortOrder === 'asc' },
    { value: 'action_desc', label: t('Action (Z-A)'), current: sortBy === 'action' && sortOrder === 'desc' },
  ];

  const buildParams = useCallback((pageNum: number): AuditLogQueryParams => {
    const params: AuditLogQueryParams = {
      page: pageNum,
      per_page: perPage,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    if (searchQuery) params.search = searchQuery;
    if (selectedFilters.entity_type?.length > 0) params.entity_type = selectedFilters.entity_type[0];
    if (selectedFilters.action?.length > 0) params.action = selectedFilters.action[0];
    if (userOnly) params.user_only = true;

    return params;
  }, [searchQuery, selectedFilters, sortBy, sortOrder, userOnly, perPage]);

  /**
   * One page at a time — the list REPLACES its rows rather than accumulating
   * them. With a thousand-plus entries, accumulating meant twenty round-trips
   * to reach the end and every row ever fetched still in the DOM.
   */
  const loadEntries = useCallback(async (pageNum: number) => {
    setIsLoading(true);
    try {
      const params = buildParams(pageNum);
      const result = await dispatch(queryAuditLogs(params) as any);
      setEntries(result.data || []);
      setTotalPages(result.total_pages || 0);
      setTotalCount(result.total || 0);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, buildParams]);

  useEffect(() => {
    loadEntries(page);
  }, [loadEntries, page]);

  /**
   * Every control that changes WHAT is being listed also returns to page 1 —
   * filtering down to nine results while sitting on page 7 shows an empty
   * table, and the fix has to be at the control, not in an effect that would
   * fire a second request for the page you just left.
   */
  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters(prev => ({ ...prev, [filterName]: selectedValues }));
    setPage(1);
  };

  const handleSortChange = (value: string) => {
    const parts = value.split('_');
    const order = parts.pop() as 'asc' | 'desc';
    const field = parts.join('_');
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
    setSearchQuery('');
    setSortBy('created_at');
    setSortOrder('desc');
    setPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPerPage(size);
    setPage(1);
  };

  const formatEntityType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  /** Clean up raw backend details strings for display. */
  const formatDetails = (entry: AuditLogEntry): string => {
    const raw = entry.details || (entry.details_json as any)?.description || '';
    if (!raw) return '—';

    // "Logged out all sessions (count=N)" → human-readable
    const logoutMatch = raw.match(/^Logged out all sessions \(count=(\d+)\)$/);
    if (logoutMatch) {
      const count = parseInt(logoutMatch[1], 10);
      if (count === 0) return 'Logged out all sessions (no active sessions)';
      if (count === 1) return 'Logged out 1 session across all devices';
      return `Logged out ${count} sessions across all devices`;
    }

    // "Permanently deleted: N folder(s), M file(s)" → cleaner
    const permDelMatch = raw.match(/^Permanently deleted: (\d+) folder\(s\), (\d+) file\(s\)$/);
    if (permDelMatch) {
      const folders = parseInt(permDelMatch[1], 10);
      const files = parseInt(permDelMatch[2], 10);
      const parts: string[] = [];
      if (folders) parts.push(`${folders} folder${folders !== 1 ? 's' : ''}`);
      if (files) parts.push(`${files} file${files !== 1 ? 's' : ''}`);
      return parts.length ? `Permanently deleted ${parts.join(' and ')}` : t('Permanently deleted items');
    }

    // "Logged out session XXXX" → just show as-is but clean
    const sessionMatch = raw.match(/^Logged out session (.{8})$/);
    if (sessionMatch) {
      return 'Logged out a session';
    }

    return raw;
  };

  const columns = [
    t('Timestamp'),
    t('User'),
    t('Resource Type'),
    t('Action'),
    t('Details'),
  ];

  const tableData = entries.map((entry) => ({
    id: entry.id,
    _entityType: entry.entity_type,
    _entityUuid: entry.entity_uuid,
    _entry: entry,
    [t('Timestamp')]: (
      <Tooltip title={formatDateTime(entry.created_at)} arrow placement="top">
        <span className="block truncate text-sm text-fg-body">
          {formatDateTime(entry.created_at)}
        </span>
      </Tooltip>
    ),
    [t('User')]: entry.user_name ? (
      // The audit row names whoever acted but carries no picture — the
      // directory supplies one where the name matches somebody in the org.
      <PersonCell
        person={{ name: entry.user_name }}
        directory={avatarDirectory}
        size="h-6 w-6"
      />
    ) : (
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-hairline-soft bg-surface-sunken">
          <PiUserLight className="h-3.5 w-3.5 text-fg-muted" />
        </div>
        <span className="truncate text-sm text-fg-body">{t('System')}</span>
      </div>
    ),
    [t('Resource Type')]: (
      <div className="flex min-w-0 flex-col gap-0.5">
        <Badge color={getEntityBadgeColor(entry.entity_type)}>
          {formatEntityType(entry.entity_type)}
        </Badge>
        {entry.entity_created_by_name && (
          <span className="truncate text-[11px] text-fg-muted">
            {t('Created by')} {entry.entity_created_by_name}
            {entry.entity_created_by_role && (
              <span className={entry.entity_created_by_role === 'Admin' ? 'text-warning-500' : ''}>
                {' '}· {entry.entity_created_by_role}
              </span>
            )}
          </span>
        )}
      </div>
    ),
    [t('Action')]: (
      <Badge color={getActionBadgeColor(entry.action)}>
        {formatAction(entry.action)}
      </Badge>
    ),
    [t('Details')]: (
      <span className="block truncate text-sm text-fg-muted" title={formatDetails(entry)}>
        {formatDetails(entry)}
      </span>
    ),
  }));

  const actions = [
    {
      label: t('View Details'),
      onClick: (row: any) => {
        dispatch(showAuditLogDetailModal(row._entry));
      },
      icon: <PiEyeLight className="w-4 h-4" />,
    },
    {
      label: t('View Activity'),
      onClick: (row: any) => {
        setAuditTarget({
          entityType: row._entityType,
          entityId: row._entityUuid,
          name: `${formatEntityType(row._entityType)} Activity`,
        });
      },
      icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
    },
  ];

  return (
    <>
    {/* The list owns the only scroll region: the page (or the settings content
        pane it is embedded in) never scrolls under its own header. */}
    <div className={LIST_SECTION_SHELL}>
      <div data-tour-id="audit-log-area" className={LIST_SECTION_CHROME}>
      {!embedded && <Header />}

      <FilterBarWithTabs
        itemCount={entries.length}
        totalItemCount={totalCount}
        itemPage={page}
        itemPageSize={perPage}
        itemCountLabel="entries"
        filters={filters}
        sortOptions={sortOptions}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        onClearAllFilters={handleClearFilters}
        // onRefresh={() => loadEntries(1)}
      />
      </div>

      {/* `bodyClassName` is not cosmetic here: GlassCard always wraps its
          children in a body div, and an UNSTYLED body is a plain block whose
          height is its content. The table's `h-full` then resolves against
          `auto`, the table grows to all fifty rows, and the card clips them
          with nothing to scroll — the reported bug, exactly. */}
      <GlassCard
        padding="none"
        className={LIST_SECTION_SURFACE}
        bodyClassName="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <Table
          tableId="audit-log"
          columns={columns}
          data={tableData}
          actions={actions}
          isLoading={isLoading && entries.length === 0}
          onRowClick={(row) => {
            dispatch(showAuditLogDetailModal(row._entry));
          }}
          emptyState={{
            icon: <PiClockCounterClockwiseLight />,
            headline: t('No audit log entries found'),
            subtext: t('Activity will appear here as actions are performed across the platform.'),
          }}
          total_items={totalCount}
          total_showing_items={entries.length}
          setIsShowing={true}
          showingLabel={t('entries')}
          resizableColumns={true}
          defaultColumnWidths={{
            [t('Timestamp')]: 180,
            [t('User')]: 200,
            [t('Resource Type')]: 160,
            [t('Action')]: 160,
            [t('Details')]: 280,
            'Actions': 48,
          }}
          isLoadingMore={isLoading}
          pagination={{
            page,
            totalPages,
            onPageChange: setPage,
            pageSize: perPage,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onPageSizeChange: handlePageSizeChange,
            disabled: isLoading,
          }}
          min="min-h-0"
          max="max-h-full"
        />
      </GlassCard>

      {/* Side panel for entity activity */}
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType={auditTarget.entityType}
            entityId={auditTarget.entityId}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}
    </div>
    <FeatureTour
      steps={activityLogTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default AuditLogPage;
